import { useState, useEffect, useRef } from "react";
import { KeyRound } from "lucide-react";
import styles from "@/components/account/AccountVisual.module.css";
import { Button } from "@/components/ax/Button";
import { Sheet } from "@/components/ax/Sheet";
import { Card } from "@/components/account/PreferencesCard";
import { useI18n } from "@/i18n";
import { auth } from "@/lib/firebase";
import { accountAccess } from "@/lib/accountAccess";
import { subscriptionPresentation } from "@/lib/subscriptionPresentation";
import {
  syncSubscriptionFromFirestore,
  fmtDate,
} from "@/lib/subscription";

export const QuietConfirm = ({
  title,
  body,
  confirmLabel,
  keepLabel,
  onConfirm,
  onClose,
  disabled,
  busy,
  error,
  children,
  testId,
}) => {
  const triggerRef = useRef(document.activeElement);
  return (
  <Sheet open onOpenChange={(o) => !o && onClose()} title={title} testId={testId} className={styles.dialog} returnFocusRef={triggerRef}>
    <p className="m-0 text-base leading-6 text-ax-text-2">{body}</p>
    {children}
    {error && (
      <p
        role="alert"
        data-testid={`${testId}-error`}
        className="m-0 mt-3 text-sm leading-5 text-ax-error"
      >
        {error}
      </p>
    )}
    <div className="mt-6 flex flex-wrap items-center gap-3">
      <Button
        variant="danger"
        disabled={disabled}
        busy={busy}
        onClick={onConfirm}
        data-testid={`${testId}-confirm`}
      >
        {confirmLabel}
      </Button>
      <Button variant="ghost" onClick={onClose} data-testid={`${testId}-keep`}>
        {keepLabel}
      </Button>
    </div>
  </Sheet>
  );
};

const Row = ({ k, children, testId }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
    <dt className="text-sm leading-5 text-ax-muted">{k}</dt>
    <dd
      data-testid={testId}
      className="m-0 font-mono text-[13px] leading-[18px] text-ax-text [overflow-wrap:anywhere]"
    >
      {children}
    </dd>
  </div>
);

export const SubscriptionCard = () => {
  const { t, lang } = useI18n();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    syncSubscriptionFromFirestore(auth.currentUser?.uid).then((latest) => {
      if (alive) { setSub(latest); setLoading(false); setNow(Date.now()); }
    }).catch(() => { if (alive) { setSub(null); setLoading(false); } });
    return () => { alive = false; };
  }, [retry]);

  useEffect(() => {
    const endsAt = sub?.status === "trialing" ? sub.trialEndsAt : sub?.status === "canceled" ? sub.currentPeriodEnd : null;
    const remaining = endsAt ? new Date(endsAt).getTime() - Date.now() : NaN;
    if (!(remaining > 0)) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(remaining + 1, 2147483647));
    return () => clearTimeout(timer);
  }, [sub, now]);

  const access = accountAccess(sub, now);
  const isPastDue = access.status === "past_due";
  const isPilot = access.accountType === "pilot";
  const premium = !isPilot && access.allowed && ["active", "canceled"].includes(access.status);
  const view = subscriptionPresentation(sub, now);
  const label = loading ? t("acc.accessLoading") : !sub ? t("acc.accessUnavailable") :
    t(view.labelKey);
  const isNoneOrExpired = sub && !loading && !isPilot && !access.allowed && !isPastDue;

  return (
    <Card id="acc-sub" title={t("acc.sub")} summary={label} testId="account-subscription" icon={KeyRound}>
      {loading ? <p role="status" className="m-0 text-sm text-ax-muted">{t("acc.accessLoading")}</p> : !sub ? (
        <div><p role="alert" className="m-0 text-sm text-ax-error" data-testid="subscription-error">{t("acc.accessUnavailable")}</p><Button variant="ghost" onClick={() => setRetry((n) => n + 1)}>{t("acc.retry")}</Button></div>
      ) : <>
      <dl className="m-0 flex flex-col gap-2">
        <Row k={t("acc.status")} testId="sub-status">
          {label}
        </Row>
        <Row k={t("acc.accountType")} testId="sub-account-type">{t(isPilot ? "acc.accessPilot" : "acc.accountPublic")}</Row>
        {premium && ["monthly", "annual"].includes(sub.plan) && <Row k={t("acc.period")} testId="sub-period">{t(`plan.${sub.plan}`)}</Row>}
        {!isPilot && access.status === "trialing" && <Row k={t("acc.trialUntil")} testId="sub-trial-end">{fmtDate(lang, sub.trialEndsAt)}</Row>}
      </dl>

      {/* Status pilot: menampilkan undangan, tanpa plan dan tanpa tanggal */}
      {isPilot && (
        <p
          data-testid="subscription-pilot-notice"
          className="m-0 mt-3 text-sm leading-5 text-ax-text-2"
        >
          {t("acc.pilotPermanent")}
        </p>
      )}

      {/* Status past_due: tetap kalimat terkunci dari sesi cabut grant */}
      {isPastDue && (
        <p
          data-testid="subscription-locked"
          className="m-0 mt-3 text-sm leading-5 text-ax-text-2"
        >
          {t("locked")}
        </p>
      )}

      {/* Explain actual public access without promising an invitation or payment. */}
      {isNoneOrExpired && (
        <>
          <p
            data-testid="subscription-invitation-notice"
            className="m-0 mt-3 text-sm leading-5 text-ax-text-2"
          >
            {t(view.noticeKey)}
          </p>
          <div className="mt-4">
            <a
              href="mailto:halo@axoris.id"
              data-testid="sub-invitation-cta"
              className="inline-flex min-h-[44px] items-center text-sm font-medium text-ax-link hover:underline"
            >
              {t("sub.contactSupport")} →
            </a>
          </div>
        </>
      )}

      </>}
    </Card>
  );
};
